/**
 * ApiNeededNotice — a prominent, unmissable panel shown IN PLACE of a screen's
 * content when that screen has no backend API to populate it yet. Makes the gap
 * explicit (what's needed + why) so it's never mistaken for "broken" or empty.
 */
import React from 'react'
import {Box, Typography} from '@mui/material'
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import {FONT, SIZE, WEIGHT} from '../styles/foundation/typography'
import {NAVY, SUBTLE, WHITE, WARNING} from '../styles/foundation/colors'
import {SHADOW} from '../styles/foundation/effects'

export default function ApiNeededNotice({title, summary, points = [], endpoint, status = 'Backend API needed', footnote}) {
  const footer = footnote || 'This screen will populate automatically once the above backend API is available. Tracked in docs/APIS_NEEDED.html.'
  return (
    <Box sx={{
      bgcolor: '#fff8f0', border: `1px solid ${WARNING}55`, borderLeft: `5px solid ${WARNING}`,
      borderRadius: '12px', p: {xs: 2.5, md: 3.5}, boxShadow: SHADOW.card,
    }}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5}}>
        <Box sx={{width: 44, height: 44, borderRadius: '50%', bgcolor: `${WARNING}1f`, color: WARNING, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '& svg': {fontSize: 26}}}>
          <BuildCircleOutlinedIcon />
        </Box>
        <Box>
          <Typography sx={{fontFamily: FONT, fontSize: '0.62rem', fontWeight: WEIGHT.bold, color: WARNING, textTransform: 'uppercase', letterSpacing: '0.06em'}}>
            {status}
          </Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.extra, color: NAVY, lineHeight: 1.2}}>
            {title}
          </Typography>
        </Box>
      </Box>

      {summary && (
        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, mb: points.length ? 1.5 : 0, maxWidth: 720}}>
          {summary}
        </Typography>
      )}

      {points.length > 0 && (
        <Box component='ul' sx={{m: 0, mb: endpoint ? 1.5 : 0, pl: 0, listStyle: 'none'}}>
          {points.map((p, i) => (
            <Box component='li' key={i} sx={{display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.6}}>
              <ArrowRightAltIcon sx={{fontSize: 18, color: WARNING, mt: 0.2, flexShrink: 0}} />
              <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>{p}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {endpoint && (
        <Box sx={{bgcolor: WHITE, border: `1px solid ${WARNING}40`, borderRadius: '8px', px: 1.5, py: 1, display: 'inline-block'}}>
          <Typography sx={{fontFamily: FONT, fontSize: '0.62rem', fontWeight: WEIGHT.bold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.3}}>
            Endpoint
          </Typography>
          <Typography sx={{fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: SIZE['2xs'], color: NAVY, wordBreak: 'break-word'}}>
            {endpoint}
          </Typography>
        </Box>
      )}

      <Typography sx={{fontFamily: FONT, fontSize: '0.66rem', color: SUBTLE, mt: 2, fontStyle: 'italic'}}>
        {footer}
      </Typography>
    </Box>
  )
}
